import { devWarn } from './devLog';
import {
  getSaleCommittedAloneCap,
  getSaleGuaranteedDeparturePricing,
  getSaleSharedPerPersonRate,
  isSalePricingActive,
} from './salePricingOverlay';

function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Pricing instant is trip open time (trip_requests.created_at), not hold authorization time:
 * every rider on a trip must share one frozen sale version, and a mid-sale tune between create
 * and pay must not silently move the trip onto a new ladder.
 */
export function resolveTripPricingInstant(trip) {
  const raw = trip?.created_at;
  if (raw == null || String(raw).trim() === '') {
    throw new Error('Trip created_at is required for shared-trip pricing');
  }
  const ms = Date.parse(String(raw));
  if (!Number.isFinite(ms)) {
    throw new Error(`Trip created_at is not parseable for pricing: ${raw}`);
  }
  return new Date(ms);
}

// Segment-based pricing tables
const PRICING = {
  "NairobiToArusha": {
    1: 290, 2: 150, 3: 105, 4: 80, 5: 65, 6: 55
  },
  "ArushaToMoshi": {
    1: 135, 2: 67.50, 3: 45, 4: 33.75, 5: 27, 6: 22.50
  },
  "NairobiToMoshi": {
    1: 420, 2: 210, 3: 140, 4: 105, 5: 84, 6: 70 // unused: full-route fares use per-leg sums
  },
  "ArushaToNairobi": {
    1: 290, 2: 150, 3: 105, 4: 80, 5: 65, 6: 55
  },
  "MoshiToArusha": {
    1: 135, 2: 67.50, 3: 45, 4: 33.75, 5: 27, 6: 22.50
  },
  "MoshiToNairobi": {
    1: 420, 2: 210, 3: 140, 4: 105, 5: 84, 6: 70 // unused: full-route fares use per-leg sums
  }
};

/** Seats in the standard vehicle. The fare ladder above is priced to this. */
export const SHARED_TRIP_STANDARD_CAPACITY = 6;

/** Seats in the larger vehicle used when demand overflows the standard one. */
export const SHARED_TRIP_OVERFLOW_CAPACITY = 13;

/** True when occupancy has outgrown the standard vehicle and ops must upsize. */
export function needsLargeVehicle(seatsOccupied) {
  return Number(seatsOccupied) > SHARED_TRIP_STANDARD_CAPACITY;
}

// Get per-person rate for a segment and number of seats.
// Mirror of getPerPersonRate in supabase/functions/_shared/tripPricing.ts —
// the server copy is authoritative; this one drives display only.
function getPerPersonRate(segment, seatsOccupied, pricingAt) {
  const pricing = PRICING[segment];
  if (!pricing) {
    throw new Error(`No pricing table for segment: ${segment}`);
  }

  // Fares flatten at the standard-vehicle rate: seats 7-13 each pay the 6-seat
  // price and the surplus funds the larger vehicle. Only the upper bound is
  // clamped, so 0/negative/fractional counts still throw.
  const tier = seatsOccupied > SHARED_TRIP_STANDARD_CAPACITY
    ? SHARED_TRIP_STANDARD_CAPACITY
    : seatsOccupied;

  const saleRate = getSaleSharedPerPersonRate(segment, tier, pricingAt);
  if (saleRate != null) return saleRate;

  const rate = pricing[tier];
  if (rate == null) {
    throw new Error(`No pricing for ${seatsOccupied} seats on ${segment}`);
  }

  return rate;
}

function isThreeStopCorridorRoute(route) {
  return route.length === 3 &&
    ((route[0] === 'Nairobi' && route[2] === 'Moshi') ||
      (route[0] === 'Moshi' && route[2] === 'Nairobi'));
}

function getDirectFullRoutePerPersonRate(route, fullRouteOccupancy, pricingAt) {
  const directSegment = route[0] === 'Nairobi' ? 'NairobiToMoshi' : 'MoshiToNairobi';
  const tier = Math.max(
    1,
    Math.min(
      SHARED_TRIP_STANDARD_CAPACITY,
      fullRouteOccupancy > SHARED_TRIP_STANDARD_CAPACITY
        ? SHARED_TRIP_STANDARD_CAPACITY
        : fullRouteOccupancy,
    ),
  );
  return getPerPersonRate(directSegment, tier, pricingAt);
}

// Calculate cost for a user joining a specific segment
export async function calculateUserCost(trip, userSegment, userGroupSize, dbHelpers, excludeUserId = null) {
  if (!trip || !userSegment || !userGroupSize) {
    console.error('calculateUserCost: Missing required parameters', { trip, userSegment, userGroupSize });
    throw new Error('Missing required parameters for cost calculation');
  }
  
  const { direction, id: tripId, pickup_location, dropoff_location } = trip;
  
  if (!direction || !pickup_location || !dropoff_location) {
    console.error('calculateUserCost: Missing trip details', { direction, pickup_location, dropoff_location });
    throw new Error('Invalid trip details for cost calculation');
  }
  
  // Handle mock trips (for pricing preview) - skip database calls
  if (tripId === 'temp') {
    
    // Define the route stops based on actual trip route
    let route;
    if (pickup_location === 'Moshi' && dropoff_location === 'Nairobi') {
      route = ['Moshi', 'Arusha', 'Nairobi'];
    } else if (pickup_location === 'Nairobi' && dropoff_location === 'Moshi') {
      route = ['Nairobi', 'Arusha', 'Moshi'];
    } else if (pickup_location === 'Arusha' && dropoff_location === 'Nairobi') {
      route = ['Moshi', 'Arusha', 'Nairobi'];
    } else if (pickup_location === 'Nairobi' && dropoff_location === 'Arusha') {
      route = ['Nairobi', 'Arusha', 'Moshi'];
    } else if (pickup_location === 'Moshi' && dropoff_location === 'Arusha') {
      route = ['Moshi', 'Arusha', 'Nairobi'];
    } else if (pickup_location === 'Arusha' && dropoff_location === 'Moshi') {
      route = ['Nairobi', 'Arusha', 'Moshi'];
    } else {
      route = ['Moshi', 'Arusha', 'Nairobi'];
    }
    
    // Create passenger object for the new user
    const newPassenger = {
      id: 'new_user',
      from: getPassengerFrom(userSegment, direction),
      to: getPassengerTo(userSegment, direction, pickup_location, dropoff_location),
      count: userGroupSize
    };
    
    // For mock trips, simulate existing participants based on seats_filled
    const existingSeats = (trip.seats_filled || 0) - userGroupSize;
    let existingPassengers = [];
    
    if (existingSeats > 0) {
      // Create a mock existing passenger with the same route as the new user
      existingPassengers = [{
        id: 'existing_user',
        from: getPassengerFrom(userSegment, direction),
        to: getPassengerTo(userSegment, direction, pickup_location, dropoff_location),
        count: existingSeats
      }];
    }
    
    const allPassengers = [...existingPassengers, newPassenger];
    const totals = calculateTotalCost(allPassengers, route, resolveTripPricingInstant(trip));
    
    return totals['new_user'] || 0;
  }
  
  // Get all participants for this trip.
  // Status filter (authorized/paid only) lives in trip_participants_public via getTripParticipants —
  // keep server FARE_COUNTED_STATUSES in tripPricing.ts aligned with that view.
  const { data: participants } = await dbHelpers.getTripParticipants(tripId);
  
  // Define the route stops based on actual trip route
  let route;
  if (pickup_location === 'Moshi' && dropoff_location === 'Nairobi') {
    // Moshi to Nairobi route
    route = ['Moshi', 'Arusha', 'Nairobi'];
  } else if (pickup_location === 'Nairobi' && dropoff_location === 'Moshi') {
    // Nairobi to Moshi route
    route = ['Nairobi', 'Arusha', 'Moshi'];
  } else if (pickup_location === 'Arusha' && dropoff_location === 'Nairobi') {
    // Arusha to Nairobi route
    route = ['Moshi', 'Arusha', 'Nairobi'];
  } else if (pickup_location === 'Nairobi' && dropoff_location === 'Arusha') {
    // Nairobi to Arusha route
    route = ['Nairobi', 'Arusha', 'Moshi'];
  } else if (pickup_location === 'Moshi' && dropoff_location === 'Arusha') {
    // Moshi to Arusha route
    route = ['Moshi', 'Arusha', 'Nairobi'];
  } else if (pickup_location === 'Arusha' && dropoff_location === 'Moshi') {
    // Arusha to Moshi route
    route = ['Nairobi', 'Arusha', 'Moshi'];
  } else {
    // Default fallback
    route = ['Moshi', 'Arusha', 'Nairobi'];
  }
  
  // Map existing participants to passenger objects with better error handling
  const passengers = participants
    .filter(p => p && p.segment)
    .map(p => {
      try {
        if (!p.segment || typeof p.segment !== 'string') {
          devWarn(`Skipping participant ${p.id} - invalid segment: ${p.segment}`);
          return null;
        }
        
        if (!direction || typeof direction !== 'string') {
          devWarn(`Skipping participant ${p.id} - invalid direction: ${direction}`);
          return null;
        }
        
        const from = getPassengerFrom(p.segment, direction);
        const to = getPassengerTo(p.segment, direction, pickup_location, dropoff_location);
        
        if (!from || !to) {
          devWarn(`Skipping participant ${p.id} - invalid segment: ${p.segment}`);
          return null;
        }
        
        const passenger = {
          id: String(p.id),
          from,
          to,
          count: p.group_size || 1
        };
        
        return passenger;
      } catch (error) {
        console.error(`Error processing participant ${p?.id || 'unknown'}:`, error);
        return null;
      }
    })
    .filter(p => p !== null);
  
  
  // Add the new user with better error handling
  let newPassenger;
  try {
    // Validate inputs before calling helper functions
    if (!userSegment || typeof userSegment !== 'string') {
      throw new Error(`Invalid user segment: ${userSegment}`);
    }
    
    if (!direction || typeof direction !== 'string') {
      throw new Error(`Invalid direction: ${direction}`);
    }
    
    const from = getPassengerFrom(userSegment, direction);
    const to = getPassengerTo(userSegment, direction, pickup_location, dropoff_location);
    
    if (!from || !to) {
      throw new Error(`Could not determine from/to locations for segment: ${userSegment}, direction: ${direction}`);
    }
    
    newPassenger = {
      id: excludeUserId ? `pending_${excludeUserId}` : 'new_user',
      from,
      to,
      count: userGroupSize
    };
  } catch (error) {
    console.error('Error creating new passenger object:', error);
    throw new Error(`Failed to create passenger object: ${error.message}`);
  }
  
  
  // Remove the excluded participant if recalculating
  const excludeParticipantId = excludeUserId
    ? participants.find((p) => p.user_id === excludeUserId)?.id
    : null;
  const filteredPassengers = excludeParticipantId != null
    ? passengers.filter(p => p.id !== String(excludeParticipantId))
    : passengers;
  const allPassengers = [...filteredPassengers, newPassenger];
  
  
  // Calculate total cost using the core algorithm
  const totals = calculateTotalCost(allPassengers, route, resolveTripPricingInstant(trip));
  
  // Return the total for the new user
  return totals[newPassenger.id] || 0;
}

// Calculate cost for the trip creator (authorization ceiling / committed cap — not solo ladder)
export function getCommittedAloneCap(segment, pricingAt) {
  const saleCap = getSaleCommittedAloneCap(segment, pricingAt);
  if (saleCap != null) return saleCap;

  switch (segment) {
    case 'NairobiToArusha':
    case 'ArushaToNairobi':
      return 275;
    case 'NairobiToMoshi':
    case 'MoshiToNairobi':
      return 420;
    case 'ArushaToMoshi':
    case 'MoshiToArusha':
      return 150;
    default:
      throw new Error(`Unknown segment for committed-alone cap: ${segment}`);
  }
}

function creatorBoardedSegments(trip, creatorSegment) {
  const route = buildRouteFromTrip(trip);
  const from = getPassengerFrom(creatorSegment, trip.direction);
  const to = getPassengerTo(
    creatorSegment,
    trip.direction,
    trip.pickup_location,
    trip.dropoff_location,
  );

  const segments = [];
  let inPath = false;
  for (let i = 0; i < route.length - 1; i += 1) {
    const segFrom = route[i];
    const segTo = route[i + 1];
    if (segFrom === from) inPath = true;
    if (inPath) segments.push(`${segFrom}To${segTo}`);
    if (segTo === to) break;
  }
  if (segments.length === 0) {
    throw new Error(`Cannot resolve boarded segments for ${creatorSegment}`);
  }
  return segments;
}

function getGuaranteedDeparturePrice(segment, groupSize, pricingAt) {
  const size = Math.max(1, groupSize);
  const mode = getSaleGuaranteedDeparturePricing(pricingAt);
  if (mode === 'partyLadder') {
    const tier = Math.min(size, 6);
    const rate = getSaleSharedPerPersonRate(segment, tier, pricingAt);
    if (rate != null) {
      return round2(rate * size);
    }
  }
  return getCommittedAloneCap(segment, pricingAt);
}

export function buildRouteFromTrip(trip) {
  const { pickup_location, dropoff_location, direction } = trip;
  if (pickup_location === 'Moshi' && dropoff_location === 'Nairobi') {
    return ['Moshi', 'Arusha', 'Nairobi'];
  }
  if (pickup_location === 'Nairobi' && dropoff_location === 'Moshi') {
    return ['Nairobi', 'Arusha', 'Moshi'];
  }
  if (pickup_location === 'Arusha' && dropoff_location === 'Nairobi') {
    return ['Moshi', 'Arusha', 'Nairobi'];
  }
  if (pickup_location === 'Nairobi' && dropoff_location === 'Arusha') {
    return ['Nairobi', 'Arusha', 'Moshi'];
  }
  if (pickup_location === 'Moshi' && dropoff_location === 'Arusha') {
    return ['Moshi', 'Arusha', 'Nairobi'];
  }
  if (pickup_location === 'Arusha' && dropoff_location === 'Moshi') {
    return ['Nairobi', 'Arusha', 'Moshi'];
  }
  return direction === 'NairobiToMoshi'
    ? ['Nairobi', 'Arusha', 'Moshi']
    : ['Moshi', 'Arusha', 'Nairobi'];
}

function computeNotCommittedCreatorCeiling(trip, creatorSegment, groupSize, pricingAt) {
  const size = Math.max(1, groupSize);

  if (
    isSalePricingActive(pricingAt)
    && (creatorSegment === 'NairobiToMoshi' || creatorSegment === 'MoshiToNairobi')
  ) {
    const tier = Math.max(1, Math.min(SHARED_TRIP_STANDARD_CAPACITY, size));
    return getPerPersonRate(creatorSegment, tier, pricingAt) * size;
  }

  const segments = creatorBoardedSegments(trip, creatorSegment);
  let total = 0;
  segments.forEach((seg, index) => {
    const occupancyTier = index === 0 ? size + 1 : size;
    const tier = Math.max(1, Math.min(SHARED_TRIP_STANDARD_CAPACITY, occupancyTier));
    const rate = getPerPersonRate(seg, tier, pricingAt);
    total += rate * size;
  });
  return total;
}

/** Display mirror of computeCreatorCeiling in tripPricing.ts (server is authoritative). */
export function computeCreatorCeiling({ committed, groupSize, creatorSegment, trip }) {
  const pricingAt = resolveTripPricingInstant(trip);
  const guaranteed = getGuaranteedDeparturePrice(creatorSegment, groupSize, pricingAt);
  if (committed) {
    return guaranteed;
  }
  return Math.min(
    computeNotCommittedCreatorCeiling(trip, creatorSegment, groupSize, pricingAt),
    guaranteed,
  );
}

export async function calculateCreatorCost(trip, creatorSegment, creatorGroupSize, dbHelpers) {
  const committed = Boolean(trip.creator_committed_no_extra_riders);
  return computeCreatorCeiling({
    committed,
    groupSize: creatorGroupSize,
    creatorSegment,
    trip,
  });
}

const FARE_COUNTED_STATUSES = new Set(['authorized', 'paid']);

function isParticipantCountedForPricing(participant, options = {}) {
  if (options.countAsCommittedIds?.includes(participant.id)) {
    return true;
  }
  return FARE_COUNTED_STATUSES.has(participant.payment_status);
}

function participantPricingKey(participant) {
  return `p${participant.id}`;
}

function participantToPassengerRow(participant, trip) {
  if (!participant?.segment) return null;
  const from = getPassengerFrom(participant.segment, trip.direction);
  const to = getPassengerTo(
    participant.segment,
    trip.direction,
    trip.pickup_location,
    trip.dropoff_location,
  );
  if (!from || !to) return null;
  return {
    id: participantPricingKey(participant),
    from,
    to,
    count: participant.group_size || 1,
  };
}

function hasNonCreatorAdmitted(participants, creatorId, options) {
  return participants.some(
    (participant) =>
      participant.user_id !== creatorId
      && isParticipantCountedForPricing(participant, options),
  );
}

function applyCommittedAloneCap(trip, participants, amounts, pricingAt, options) {
  if (!trip.creator_committed_no_extra_riders || !trip.creator_id) {
    return;
  }
  if (hasNonCreatorAdmitted(participants, trip.creator_id, options)) {
    return;
  }

  const creator = participants.find(
    (participant) =>
      participant.user_id === trip.creator_id
      && isParticipantCountedForPricing(participant, options),
  );
  if (!creator?.segment) {
    return;
  }

  amounts.set(
    creator.id,
    getGuaranteedDeparturePrice(creator.segment, creator.group_size ?? 1, pricingAt),
  );
}

function applyNotGuaranteedCreatorFareCap(trip, participants, amounts, pricingAt, options) {
  if (!trip.creator_id || trip.creator_committed_no_extra_riders) {
    return;
  }

  const creator = participants.find(
    (participant) =>
      participant.user_id === trip.creator_id
      && isParticipantCountedForPricing(participant, options),
  );
  if (!creator?.segment) {
    return;
  }

  const cap = getGuaranteedDeparturePrice(
    creator.segment,
    creator.group_size ?? 1,
    pricingAt,
  );
  const current = amounts.get(creator.id) ?? 0;
  amounts.set(creator.id, Math.min(current, cap));
}

/** Sync mirror of server computeParticipantAmounts — display and refund suggestions only. */
export function computeParticipantLadderAmounts(trip, participants, options = {}) {
  const pricingAt = resolveTripPricingInstant(trip);
  const route = buildRouteFromTrip(trip);
  const counted = participants.filter((p) => isParticipantCountedForPricing(p, options));
  const passengers = counted
    .map((p) => participantToPassengerRow(p, trip))
    .filter((p) => p !== null);

  const totals = calculateTotalCost(passengers, route, pricingAt);
  const amounts = new Map();

  for (const participant of participants) {
    if (!isParticipantCountedForPricing(participant, options)) {
      amounts.set(participant.id, 0);
      continue;
    }
    amounts.set(participant.id, totals[participantPricingKey(participant)] ?? 0);
  }

  applyCommittedAloneCap(trip, participants, amounts, pricingAt, options);
  applyNotGuaranteedCreatorFareCap(trip, participants, amounts, pricingAt, options);
  return amounts;
}

/** Sync mirror of server computeJoinerAmount — browse join-now estimates only. */
export function estimateJoinerAmount(
  trip,
  participants,
  joinerSegment,
  joinerGroupSize = 1,
  joinerId = -1,
  options = {},
) {
  const synthetic = {
    id: joinerId,
    segment: joinerSegment,
    group_size: joinerGroupSize,
    payment_status: 'authorized',
  };

  const withoutJoiner = joinerId > 0
    ? participants.filter((p) => p.id !== joinerId)
    : participants;

  const amounts = computeParticipantLadderAmounts(trip, [...withoutJoiner, synthetic], {
    countAsCommittedIds: [joinerId, ...(options.countAsCommittedIds ?? [])],
  });
  return amounts.get(joinerId) ?? 0;
}

// Core pricing algorithm
function calculateTotalCost(passengers, route, pricingAt) {
  
  // Validate inputs
  if (!passengers || !Array.isArray(passengers) || passengers.length === 0) {
    devWarn('calculateTotalCost: No valid passengers provided');
    return {};
  }
  
  if (!route || !Array.isArray(route) || route.length < 2) {
    devWarn('calculateTotalCost: Invalid route provided');
    return {};
  }
  
  // Filter out invalid passengers with more detailed logging
  const validPassengers = passengers.filter(p => {
    if (!p) {
      devWarn('calculateTotalCost: Skipping null/undefined passenger');
      return false;
    }
    if (!p.id) {
      devWarn('calculateTotalCost: Skipping passenger without ID:', p);
      return false;
    }
    if (!p.from) {
      devWarn('calculateTotalCost: Skipping passenger without from location:', p);
      return false;
    }
    if (!p.to) {
      devWarn('calculateTotalCost: Skipping passenger without to location:', p);
      return false;
    }
    if (!p.count) {
      devWarn('calculateTotalCost: Skipping passenger without count:', p);
      return false;
    }
    return true;
  });
  
  if (validPassengers.length === 0) {
    devWarn('calculateTotalCost: No valid passengers after filtering');
    return {};
  }

  const totals = {};
  validPassengers.forEach(p => { totals[p.id] = 0; });

  const useDirectMoshiLadder = isSalePricingActive(pricingAt) && isThreeStopCorridorRoute(route);
  const corridorStart = route[0];
  const corridorEnd = route[route.length - 1];
  const isFullRoutePassenger = (p) => p.from === corridorStart && p.to === corridorEnd;

  if (useDirectMoshiLadder) {
    const fullRoutePassengers = validPassengers.filter(isFullRoutePassenger);
    const fullRouteOccupancy = fullRoutePassengers.reduce(
      (sum, p) => sum + (parseInt(p.count, 10) || 1),
      0,
    );
    if (fullRouteOccupancy > 0) {
      const perPerson = getDirectFullRoutePerPersonRate(route, fullRouteOccupancy, pricingAt);
      fullRoutePassengers.forEach((p) => {
        totals[p.id] += perPerson * (parseInt(p.count, 10) || 1);
      });
    }
  }

  const segments = [];
  for (let i = 0; i < route.length - 1; i++) {
    segments.push(`${route[i]}To${route[i + 1]}`);
  }

  segments.forEach(seg => {
    const [start, end] = seg.split('To');
    
    
    // Who is on board for this segment?
    const onboard = validPassengers.filter(p => {
      // Additional safety check for passenger object
      if (!p || !p.from || !p.to) {
        devWarn(`Skipping invalid passenger object:`, p);
        return false;
      }
      
      const fromIndex = route.indexOf(p.from);
      const toIndex = route.indexOf(p.to);
      const segStartIndex = route.indexOf(start);
      const segEndIndex = route.indexOf(end);
      
      // Check if indices are valid
      if (fromIndex === -1 || toIndex === -1 || segStartIndex === -1 || segEndIndex === -1) {
        devWarn(`Invalid route indices for passenger ${p.id}: fromIndex=${fromIndex}, toIndex=${toIndex}, segStartIndex=${segStartIndex}, segEndIndex=${segEndIndex}`);
        return false;
      }
      
      // Passenger is onboard if they travel through this segment
      // They board at or before segment start AND alight after segment start
      const isOnboard = fromIndex <= segStartIndex && toIndex > segStartIndex;
      
      
      return isOnboard;
    }).filter(p => p !== null && p !== undefined);

    const chargeable = useDirectMoshiLadder
      ? onboard.filter((p) => !isFullRoutePassenger(p))
      : onboard;

    const seatsOccupied = onboard.reduce((sum, p) => {
      // Additional safety check for each passenger in reduce
      if (!p || !p.id || !p.count) {
        devWarn(`Skipping invalid passenger in reduce:`, p);
        return sum;
      }
      const count = parseInt(p.count) || 1;
      return sum + count;
    }, 0);
    
    if (seatsOccupied === 0 || chargeable.length === 0) return;

    const validSeatsOccupied = Math.max(1, Math.min(6, parseInt(seatsOccupied, 10) || 1));

    // Look up per-person rate
    const perPerson = getPerPersonRate(seg, validSeatsOccupied, pricingAt);
    
    chargeable.forEach((p) => {
      totals[p.id] += perPerson * p.count;
    });
  });
  
  // Return totals for all passengers
  return totals;
}

// Helper functions to map segments to from/to locations
function getPassengerFrom(segment, direction) {
  // Add comprehensive validation
  if (!segment || typeof segment !== 'string') {
    console.error('getPassengerFrom: segment is undefined or invalid:', segment);
    return 'Nairobi'; // fallback
  }
  
  if (!direction || typeof direction !== 'string') {
    console.error('getPassengerFrom: direction is undefined or invalid:', direction);
    return 'Nairobi'; // fallback
  }
  
  if (direction === 'NairobiToMoshi') {
    switch (segment) {
      case 'NairobiToArusha': return 'Nairobi';
      case 'ArushaToMoshi': return 'Arusha';
      case 'NairobiToMoshi': return 'Nairobi';
      default: 
        devWarn(`getPassengerFrom: Unknown segment '${segment}' for direction '${direction}'`);
        return 'Nairobi';
    }
  } else if (direction === 'MoshiToNairobi') {
    switch (segment) {
      case 'MoshiToArusha': return 'Moshi';
      case 'ArushaToNairobi': return 'Arusha';
      case 'MoshiToNairobi': return 'Moshi';
      default: 
        // Handle invalid segments by trying to infer the correct segment
        if (segment === 'NairobiToArusha') {
          devWarn(`getPassengerFrom: Invalid segment '${segment}' for direction '${direction}', using ArushaToNairobi instead`);
          return 'Arusha'; // For ArushaToNairobi segment
        } else if (segment === 'ArushaToMoshi') {
          devWarn(`getPassengerFrom: Invalid segment '${segment}' for direction '${direction}', using MoshiToArusha instead`);
          return 'Moshi'; // For MoshiToArusha segment
        } else if (segment === 'NairobiToMoshi') {
          devWarn(`getPassengerFrom: Invalid segment '${segment}' for direction '${direction}', using MoshiToNairobi instead`);
          return 'Moshi'; // For MoshiToNairobi segment
        }
        devWarn(`getPassengerFrom: Unknown segment '${segment}' for direction '${direction}'`);
        return 'Moshi';
    }
  } else {
    devWarn(`getPassengerFrom: Unknown direction '${direction}'`);
    return 'Nairobi';
  }
}

function getPassengerTo(segment, direction, pickup_location, dropoff_location) {
  // Add comprehensive validation
  if (!segment || typeof segment !== 'string') {
    console.error('getPassengerTo: segment is undefined or invalid:', segment);
    return 'Arusha'; // fallback
  }
  
  if (!direction || typeof direction !== 'string') {
    console.error('getPassengerTo: direction is undefined or invalid:', direction);
    return 'Arusha'; // fallback
  }
  
  if (direction === 'NairobiToMoshi') {
    switch (segment) {
      case 'NairobiToArusha': return 'Arusha';
      case 'ArushaToMoshi': return 'Moshi';
      case 'NairobiToMoshi': return 'Moshi'; // Always return Moshi for full route
      default: 
        devWarn(`getPassengerTo: Unknown segment '${segment}' for direction '${direction}'`);
        return 'Arusha';
    }
  } else if (direction === 'MoshiToNairobi') {
    switch (segment) {
      case 'MoshiToArusha': return 'Arusha';
      case 'ArushaToNairobi': return 'Nairobi';
      case 'MoshiToNairobi': return 'Nairobi'; // Always return Nairobi for full route
      default: 
        // Handle invalid segments by trying to infer the correct segment
        if (segment === 'NairobiToArusha') {
          devWarn(`getPassengerTo: Invalid segment '${segment}' for direction '${direction}', using ArushaToNairobi instead`);
          return 'Nairobi'; // For ArushaToNairobi segment
        } else if (segment === 'ArushaToMoshi') {
          devWarn(`getPassengerTo: Invalid segment '${segment}' for direction '${direction}', using MoshiToArusha instead`);
          return 'Arusha'; // For MoshiToArusha segment
        } else if (segment === 'NairobiToMoshi') {
          devWarn(`getPassengerTo: Invalid segment '${segment}' for direction '${direction}', using MoshiToNairobi instead`);
          return 'Nairobi'; // For MoshiToNairobi segment
        }
        devWarn(`getPassengerTo: Unknown segment '${segment}' for direction '${direction}'`);
        return 'Arusha';
    }
  } else {
    devWarn(`getPassengerTo: Unknown direction '${direction}'`);
    return 'Arusha';
  }
}

// Get cost per person for a specific segment
export function getSegmentCost(segment, totalPeople, pricingAt) {
  return getPerPersonRate(segment, Math.min(totalPeople, 6), pricingAt);
}

// Get available segments for a trip (origin-only: optional tripOrigin filters mid-route pickups).
export function getAvailableSegments(direction, tripOrigin = null) {
  let segments = [];
  if (direction === 'NairobiToMoshi') {
    segments = [
      { value: 'NairobiToArusha', label: 'Nairobi → Arusha', pickup: 'Nairobi' },
      { value: 'ArushaToMoshi', label: 'Arusha → Moshi', pickup: 'Arusha' },
      { value: 'NairobiToMoshi', label: 'Nairobi → Moshi (Full Route)', pickup: 'Nairobi' },
    ];
  } else if (direction === 'MoshiToNairobi') {
    segments = [
      { value: 'MoshiToArusha', label: 'Moshi → Arusha', pickup: 'Moshi' },
      { value: 'ArushaToNairobi', label: 'Arusha → Nairobi', pickup: 'Arusha' },
      { value: 'MoshiToNairobi', label: 'Moshi → Nairobi (Full Route)', pickup: 'Moshi' },
    ];
  }
  if (!tripOrigin) {
    return segments.map(({ value, label }) => ({ value, label }));
  }
  return segments
    .filter((seg) => seg.pickup === tripOrigin)
    .map(({ value, label }) => ({ value, label }));
}

// Calculate cost breakdown for a specific scenario (for testing/verification)
export function calculateScenarioCost(scenario) {
  let totalCost = 0;
  
  scenario.forEach(({ segment, people }) => {
    const segmentCost = getSegmentCost(segment, people);
    totalCost += segmentCost;
  });
  
  return totalCost;
} 

// Recalculate costs for all participants — UI preview only (no DB writes).
// Server recomputes amounts on payment confirm via stripe-payment-handler.
// Fare composition uses authorized/paid riders only — that filter lives in trip_participants_public
// (via getTripParticipants); keep server FARE_COUNTED_STATUSES aligned with that view.
export async function recalculateAllParticipantCosts(tripId, dbHelpers) {
  try {
    if (!tripId) {
      console.error('No trip ID provided for cost recalculation');
      return { data: null, error: 'No trip ID provided' };
    }

    const { data: participants, error } = await dbHelpers.getTripParticipants(tripId);
    if (error) throw error;

    const { data: trip } = await dbHelpers.getTripById(tripId);
    if (!trip) throw new Error('Trip not found');

    const route = trip.direction === 'NairobiToMoshi'
      ? ['Nairobi', 'Arusha', 'Moshi']
      : ['Moshi', 'Arusha', 'Nairobi'];

    const passengers = participants
      .filter((p) => p && p.segment)
      .map((p) => {
        try {
          const from = getPassengerFrom(p.segment, trip.direction);
          const to = getPassengerTo(p.segment, trip.direction, trip.pickup_location, trip.dropoff_location);
          if (!from || !to) return null;
          return {
            id: String(p.id),
            from,
            to,
            count: p.group_size || 1,
          };
        } catch {
          return null;
        }
      })
      .filter((p) => p !== null);

    const totals = calculateTotalCost(passengers, route, resolveTripPricingInstant(trip));

    const previewParticipants = participants.map((participant) => ({
      ...participant,
      payment_amount: totals[String(participant.id)] || 0,
    }));

    return { data: previewParticipants, error: null };
  } catch (error) {
    console.error('Error recalculating participant costs:', error);
    return { data: null, error };
  }
} 